/**
 * Admin Standard Detail Page
 *
 * Displays the details of an official standard including extracted clauses,
 * source document download link, and contextual action buttons based on status.
 *
 * Route: /admin/standards/$standardId
 */

// TODO: Add proper admin role check before allowing mutations

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation, getCurrentLanguage } from "@/packages/utils";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  FileText,
  Loader2,
  Send,
  Trash2,
  XCircle,
  ArrowDownCircle,
  RefreshCw,
} from "lucide-react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { Button } from "@/components/ui/primitives";
import { StateCard } from "@/components/ui";
import { ConfirmDeleteDialog } from "@/components/ui/ConfirmDeleteDialog";
import { ViewToggle } from "@/components/ui/PageHeader";
import { useApiMutation } from "@/packages/features-shared";
import { useState, useEffect } from "react";
import { ClauseList } from "@/components/standards/ClauseList";
import { ChecklistAccordion } from "@/components/standards/ChecklistAccordion";
import type { ChecklistTemplateData } from "@/components/standards/types";

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

type StandardDetail = NonNullable<
  FunctionReturnType<typeof api.admin_standards_queries.getById>
>;

type StandardStatus = StandardDetail["status"];

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
};

function getLanguageLabel(language: string): string {
  return LANGUAGE_LABELS[language] ?? language;
}

function getMissingTranslationTarget(languages: string[]): "en" | "de" | null {
  const normalized = new Set(
    languages.filter(
      (language): language is "en" | "de" =>
        language === "en" || language === "de",
    ),
  );

  if (normalized.size !== 1) {
    return null;
  }

  return normalized.has("en") ? "de" : "en";
}

function getPreferredLanguage(languages: string[], fallback: string): string {
  if (languages.length === 0) {
    return fallback;
  }

  if (languages.includes(fallback)) {
    return fallback;
  }

  const currentLanguage = getCurrentLanguage();
  if (languages.includes(currentLanguage)) {
    return currentLanguage;
  }

  return languages[0];
}

/* ------------------------------------------------------------------ */
/* Status badge configuration with translations                         */
/* ------------------------------------------------------------------ */

function useStatusConfig(): Record<
  StandardStatus,
  { label: string; bgColor: string; textColor: string; pulse?: boolean }
> {
  const { t } = useTranslation(["admin"]);

  return {
    uploading: {
      label: t("admin:standards.statusLabels.uploading"),
      bgColor: "rgba(161,161,166,0.15)",
      textColor: "#a1a1a6",
    },
    extracting: {
      label: t("admin:standards.statusLabels.extracting"),
      bgColor: "rgba(234,179,8,0.15)",
      textColor: "#ca8a04",
      pulse: true,
    },
    extracted: {
      label: t("admin:standards.statusLabels.extracted"),
      bgColor: "rgba(59,130,246,0.15)",
      textColor: "#3b82f6",
    },
    extraction_failed: {
      label: t("admin:standards.statusLabels.extraction_failed"),
      bgColor: "rgba(239,68,68,0.15)",
      textColor: "#ef4444",
    },
    approved: {
      label: t("admin:standards.statusLabels.approved"),
      bgColor: "rgba(99,102,241,0.15)",
      textColor: "#6366f1",
    },
    generating_checklist: {
      label: t("admin:standards.statusLabels.generating_checklist"),
      bgColor: "rgba(234,179,8,0.15)",
      textColor: "#ca8a04",
      pulse: true,
    },
    checklist_generation_failed: {
      label: t("admin:standards.statusLabels.checklist_generation_failed"),
      bgColor: "rgba(239,68,68,0.15)",
      textColor: "#ef4444",
    },
    published: {
      label: t("admin:standards.statusLabels.published"),
      bgColor: "rgba(34,197,94,0.15)",
      textColor: "#22c55e",
    },
    rejected: {
      label: t("admin:standards.statusLabels.rejected"),
      bgColor: "rgba(239,68,68,0.15)",
      textColor: "#ef4444",
    },
    translation_incomplete: {
      label: t("admin:standards.statusLabels.translation_incomplete"),
      bgColor: "rgba(234,179,8,0.15)",
      textColor: "#d97706",
    },
  };
}

function StatusBadge({ status }: { status: StandardStatus }) {
  const statusConfig = useStatusConfig();
  const config = statusConfig[status];
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium ${config.pulse ? "animate-pulse" : ""}`}
      style={{ backgroundColor: config.bgColor, color: config.textColor }}
    >
      {config.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Back Link                                                            */
/* ------------------------------------------------------------------ */

function BackLink() {
  const { t } = useTranslation(["admin"]);
  const { workspaceSlug } = useParams({ strict: false });
  return (
    <Link
      to="/$workspaceSlug/admin/standards"
      params={{ workspaceSlug: workspaceSlug ?? "" }}
      className="inline-flex items-center gap-1.5 text-sm text-secondary hover:text-primary transition-colors"
    >
      <ArrowLeft size={14} />
      {t("admin:standards.backToStandards")}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Standard Detail Header                                               */
/* ------------------------------------------------------------------ */

function StandardDetailHeader({ standard }: { standard: StandardDetail }) {
  const { t } = useTranslation(["admin"]);
  return (
    <div className="space-y-3">
      {/* Title row: template code + status badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText size={20} className="text-accent/70 shrink-0" />
          <h1 className="text-[18px] font-semibold text-primary">
            {standard.templateCode}
          </h1>
          {standard.templateDisplayName && (
            <span className="text-[14px] text-muted">
              {standard.templateDisplayName}
            </span>
          )}
        </div>
        <StatusBadge status={standard.status} />
      </div>

      {/* Info row: filename, clause count, download link */}
      <div className="flex items-center gap-4 text-sm text-secondary">
        <span className="truncate">{standard.fileName}</span>
        <span className="text-muted">
          {standard.extractedClausesCount}{" "}
          {t("admin:standards.counts.clausesSuffix")}
        </span>
        {standard.checklistItemsCount > 0 && (
          <span className="text-muted">
            {standard.checklistItemsCount}{" "}
            {t("admin:standards.counts.checklistItemsSuffix")}
          </span>
        )}
        {standard.documentDownloadUrl ? (
          <a
            href={standard.documentDownloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-accent underline hover:text-accent/80 transition-colors"
          >
            <Download size={12} />
            {t("admin:standards.counts.downloadSourceDocument")}
          </a>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Validation Panel                                                     */
/* ------------------------------------------------------------------ */

function ValidationPanel({
  standard,
  checklistData,
  checklistLanguage,
  onPublish,
  isPublishPending,
}: {
  standard: StandardDetail;
  checklistData: ChecklistTemplateData;
  checklistLanguage: string;
  onPublish: () => void;
  isPublishPending: boolean;
}) {
  const { t } = useTranslation(["admin"]);
  const coveredClauses = new Set(
    checklistData.categories?.flatMap(
      (c) => c.tasks?.flatMap((t) => t.clauseRefs ?? []) ?? [],
    ) ?? [],
  );
  // Filter clauses by the active checklist language for accurate coverage
  const languageClauses = standard.clauses.filter(
    (c) => (c.language ?? "en") === checklistLanguage,
  );
  const missingClauses = languageClauses
    .filter((c) => c.isAuditable)
    .filter((c) => !coveredClauses.has(c.clauseNumber))
    .map((c) => c.clauseNumber);
  const totalClauses = languageClauses.filter((c) => c.isAuditable).length;
  const coveragePercent =
    totalClauses > 0
      ? Math.round((coveredClauses.size / totalClauses) * 100)
      : 0;

  const totalTasks =
    checklistData.categories?.reduce(
      (sum, c) => sum + (c.tasks?.length ?? 0),
      0,
    ) ?? 0;
  const totalQuestions =
    checklistData.categories?.reduce(
      (sum, c) =>
        sum +
        (c.tasks?.reduce((s, t) => s + (t.questions?.length ?? 0), 0) ?? 0),
      0,
    ) ?? 0;

  return (
    <div className="space-y-4">
      {/* Actions */}
      {standard.status !== "published" && (
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="primary"
            icon={Send}
            onClick={onPublish}
            loading={isPublishPending}
            disabled={isPublishPending}
          >
            {t("admin:standards.actions.publish")}
          </Button>
        </div>
      )}

      {/* Coverage report */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-2">
        <h3 className="text-[13px] font-semibold text-primary">
          {t("admin:standards.coverage.title")}
        </h3>
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle size={14} className="text-green-500 shrink-0" />
          <span className="text-primary">
            {coveredClauses.size}/{totalClauses} clauses covered (
            {coveragePercent}%)
          </span>
        </div>
        {missingClauses.length > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <XCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
            <span className="text-secondary">
              {t("admin:standards.coverage.missingPrefix")}{" "}
              {missingClauses.join(", ")}
            </span>
          </div>
        )}
        <div className="text-xs text-muted">
          {t("admin:standards.counts.tasks")}: {totalTasks} &nbsp;·&nbsp;{" "}
          {t("admin:standards.counts.questions")}: {totalQuestions}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Standard Detail Actions                                              */
/* ------------------------------------------------------------------ */

function StandardDetailActions({
  standard,
  onAction,
  isPending,
}: {
  standard: StandardDetail;
  onAction: (
    action:
      | "approve"
      | "reject"
      | "publish"
      | "unpublish"
      | "delete"
      | "retryTranslation",
  ) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation(["admin"]);
  const status = standard.status;

  if (status === "extracted") {
    return (
      <div className="flex justify-end gap-3">
        <Button
          variant="danger"
          icon={XCircle}
          onClick={() => onAction("reject")}
          loading={isPending}
        >
          {t("admin:standards.actions.reject")}
        </Button>
        <Button
          variant="primary"
          icon={CheckCircle}
          onClick={() => onAction("approve")}
          loading={isPending}
        >
          {t("admin:standards.actions.approveAndGenerate")}
        </Button>
      </div>
    );
  }

  if (status === "translation_incomplete") {
    return (
      <div className="flex justify-end gap-3">
        <Button
          variant="secondary"
          icon={RefreshCw}
          onClick={() => onAction("retryTranslation")}
          loading={isPending}
        >
          {t("admin:standards.actions.retryTranslation")}
        </Button>
        <Button
          variant="primary"
          icon={CheckCircle}
          onClick={() => onAction("approve")}
          loading={isPending}
        >
          {t("admin:standards.actions.skipAndGenerate")}
        </Button>
      </div>
    );
  }

  // "approved" without checklist items means checklist generation hasn't started/completed yet
  if (
    (status === "approved" && standard.checklistItemsCount === 0) ||
    status === "generating_checklist"
  ) {
    return (
      <div className="flex justify-end">
        <div className="flex items-center gap-2 text-sm text-muted">
          <Loader2 size={14} className="animate-spin" />
          {t("admin:standards.actions.generatingChecklist")}
        </div>
      </div>
    );
  }

  if (status === "checklist_generation_failed") {
    return (
      <div className="flex items-center justify-end gap-3">
        {standard.extractionError && (
          <span className="text-sm text-red-500">
            {standard.extractionError}
          </span>
        )}
        <Button
          variant="danger"
          icon={Trash2}
          onClick={() => onAction("delete")}
          loading={isPending}
        >
          {t("admin:standards.actions.delete")}
        </Button>
      </div>
    );
  }

  if (status === "published") {
    return (
      <div className="flex justify-end gap-3">
        <Button
          variant="danger"
          icon={Trash2}
          onClick={() => onAction("delete")}
          loading={isPending}
        >
          {t("admin:standards.actions.delete")}
        </Button>
        <Button
          variant="secondary"
          icon={ArrowDownCircle}
          onClick={() => onAction("unpublish")}
          loading={isPending}
        >
          {t("admin:standards.actions.unpublish")}
        </Button>
      </div>
    );
  }

  if (status === "rejected" || status === "extraction_failed") {
    return (
      <div className="flex justify-end">
        <Button
          variant="danger"
          icon={Trash2}
          onClick={() => onAction("delete")}
          loading={isPending}
        >
          {t("admin:standards.actions.delete")}
        </Button>
      </div>
    );
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* Main Page Component                                                  */
/* ------------------------------------------------------------------ */

export function StandardDetailPage() {
  const { t } = useTranslation(["admin"]);
  const { standardId, workspaceSlug } = useParams({ strict: false });
  const workspaceId = useWorkspaceId();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<"clauses" | "checklist">(
    "clauses",
  );
  const [clauseLanguage, setClauseLanguage] = useState<string>("en");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: standard, isLoading } = useQuery(
    convexQuery(api.admin_standards_queries.getById, {
      id: standardId as Id<"officialStandards">,
    }),
  );

  const [checklistLanguage, setChecklistLanguage] = useState<string>("en");

  const { data: checklistTemplateResult } = useQuery(
    convexQuery(api.admin_standards_queries.getChecklistTemplate, {
      standardId: standardId as Id<"officialStandards">,
      language: checklistLanguage,
    }),
  );

  // checklistData is v.any() in schema, so we type it here to avoid `as` casts at usage sites
  const checklistData: ChecklistTemplateData | null =
    checklistTemplateResult?.checklistData ?? null;
  const clauseLanguages = standard
    ? [
        ...new Set(standard.clauses.map((clause) => clause.language ?? "en")),
      ].sort()
    : [];
  const checklistAvailableLanguages =
    checklistTemplateResult?.availableLanguages ?? [];
  const clauseTranslateTargetLanguage =
    getMissingTranslationTarget(clauseLanguages);
  const checklistTranslateTargetLanguage = getMissingTranslationTarget(
    checklistAvailableLanguages,
  );
  // Default to checklist tab once checklist data is available
  const hasChecklist = !!checklistData;
  useEffect(() => {
    if (hasChecklist) setActiveTab("checklist");
  }, [hasChecklist]);

  useEffect(() => {
    const preferredLanguage = getPreferredLanguage(
      clauseLanguages,
      clauseLanguage,
    );
    if (preferredLanguage !== clauseLanguage) {
      setClauseLanguage(preferredLanguage);
    }
  }, [clauseLanguage, clauseLanguages]);

  useEffect(() => {
    const preferredLanguage = getPreferredLanguage(
      checklistAvailableLanguages,
      checklistLanguage,
    );
    if (preferredLanguage !== checklistLanguage) {
      setChecklistLanguage(preferredLanguage);
    }
  }, [checklistAvailableLanguages, checklistLanguage]);

  // --- Mutation hooks (useApiMutation for makeHandler Result unwrapping) ---
  const approveMutation = useApiMutation(
    api.admin_standards_mutations.approveExtraction,
    {
      name: "approve-extraction",
      successMessage: "Extraction approved. Generating checklist...",
    },
  );

  const rejectMutation = useApiMutation(
    api.admin_standards_mutations.rejectExtraction,
    { name: "reject-extraction", successMessage: "Extraction rejected" },
  );

  const publishMutation = useApiMutation(
    api.admin_standards_mutations.publish,
    { name: "publish-standard", successMessage: "Standard published" },
  );

  const unpublishMutation = useApiMutation(
    api.admin_standards_mutations.unpublish,
    { name: "unpublish-standard", successMessage: "Standard unpublished" },
  );

  const deleteMutation = useApiMutation(
    api.admin_standards_mutations.deleteStandard,
    { name: "delete-standard", successMessage: "Standard deleted" },
  );

  const isAnyMutationPending =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    publishMutation.isPending ||
    unpublishMutation.isPending ||
    deleteMutation.isPending;

  const retryTranslationMutation = useApiMutation(
    api.admin_standards_mutations.retryTranslation,
    {
      name: "retry-translation",
      successMessage: "Retrying translation...",
    },
  );

  const requestClauseTranslationMutation = useApiMutation(
    api.admin_standards_mutations.requestClauseTranslation,
    {
      name: "request-clause-translation",
      successMessage: "Clause translation queued",
    },
  );

  const requestChecklistTranslationMutation = useApiMutation(
    api.admin_standards_mutations.requestChecklistTranslation,
    {
      name: "request-checklist-translation",
      successMessage: "Checklist translation queued",
    },
  );

  const isAnyMutationPendingWithRetry =
    isAnyMutationPending || retryTranslationMutation.isPending;
  const clauseTranslationPendingTargetLanguage =
    standard?.clauseTranslationTargetLanguage ??
    (requestClauseTranslationMutation.isPending
      ? clauseTranslateTargetLanguage
      : null);
  const checklistTranslationPendingTargetLanguage =
    standard?.checklistTranslationTargetLanguage ??
    (requestChecklistTranslationMutation.isPending
      ? checklistTranslateTargetLanguage
      : null);

  const handleAction = async (
    action:
      | "approve"
      | "reject"
      | "publish"
      | "unpublish"
      | "delete"
      | "retryTranslation",
  ) => {
    if (!standard) return;
    const id = standard._id;

    switch (action) {
      case "approve":
        await approveMutation.mutateAsync({
          standardId: id,
          locale: getCurrentLanguage(),
          workspaceId,
        });
        break;
      case "reject":
        await rejectMutation.mutateAsync({ standardId: id });
        navigate({
          to: "/$workspaceSlug/admin/standards",
          params: { workspaceSlug: workspaceSlug ?? "" },
        });
        break;
      case "publish":
        await publishMutation.mutateAsync({ standardId: id });
        break;
      case "unpublish":
        await unpublishMutation.mutateAsync({ standardId: id });
        break;
      case "retryTranslation":
        await retryTranslationMutation.mutateAsync({ standardId: id });
        break;
      case "delete":
        setDeleteDialogOpen(true);
        return;
    }
  };

  const handleConfirmDelete = async () => {
    if (!standard) return;
    await deleteMutation.mutateAsync({ standardId: standard._id });
    setDeleteDialogOpen(false);
    navigate({
      to: "/$workspaceSlug/admin/standards",
      params: { workspaceSlug: workspaceSlug ?? "" },
    });
  };

  const handleRequestClauseTranslation = async () => {
    if (!standard) return;
    await requestClauseTranslationMutation.mutateAsync({
      standardId: standard._id,
    });
  };

  const handleRequestChecklistTranslation = async () => {
    if (!standard) return;
    await requestChecklistTranslationMutation.mutateAsync({
      standardId: standard._id,
    });
  };

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="max-w-3xl space-y-6">
        <BackLink />
        <div className="flex items-center justify-center py-16">
          <StateCard variant="loading">
            <StateCard.Icon />
            <StateCard.Title>
              {t("admin:standards.loading.detailTitle")}
            </StateCard.Title>
            <StateCard.Description>
              {t("admin:standards.loading.detailDescription")}
            </StateCard.Description>
          </StateCard>
        </div>
      </div>
    );
  }

  // --- Not found state ---
  if (!standard) {
    return (
      <div className="max-w-3xl space-y-6">
        <BackLink />
        <div className="flex items-center justify-center py-16">
          <StateCard variant="empty">
            <StateCard.Icon />
            <StateCard.Title>
              {t("admin:standards.notFound.title")}
            </StateCard.Title>
            <StateCard.Description>
              {t("admin:standards.notFound.description")}
            </StateCard.Description>
          </StateCard>
        </div>
      </div>
    );
  }

  const showTabs = !!checklistData;
  // "approved" with checklist items means ready to publish (checklist generated)
  const checklistReady =
    standard.status === "approved" && standard.checklistItemsCount > 0;
  const isPublished = standard.status === "published";

  // --- Main content ---
  return (
    <div className="max-w-3xl space-y-6">
      <BackLink />
      <StandardDetailHeader standard={standard} />

      {/* Actions (shown when checklist tab is not active, or for non-checklist statuses) */}
      {(activeTab === "clauses" || !showTabs || !checklistReady) && (
        <StandardDetailActions
          standard={standard}
          onAction={handleAction}
          isPending={isAnyMutationPendingWithRetry}
        />
      )}

      {/* Tab bar */}
      {showTabs && (
        <ViewToggle
          options={[
            {
              id: "clauses",
              label: t("admin:standards.tabLabels.clauses"),
              active: activeTab === "clauses",
            },
            {
              id: "checklist",
              label: `${t("admin:standards.tabLabels.checklist")} (${standard.checklistItemsCount})`,
              active: activeTab === "checklist",
            },
          ]}
          onViewChange={(id) => setActiveTab(id as "clauses" | "checklist")}
        />
      )}

      {/* Tab content */}
      {activeTab === "clauses" || !showTabs ? (
        <div className="space-y-3">
          {(clauseLanguages.length > 1 ||
            clauseTranslateTargetLanguage ||
            clauseTranslationPendingTargetLanguage) && (
            <div className="flex flex-wrap items-center gap-3">
              {clauseLanguages.length > 1 && (
                <div className="flex gap-1 rounded-lg bg-muted/30 p-1 w-fit">
                  {clauseLanguages.map((language) => (
                    <button
                      key={language}
                      type="button"
                      onClick={() => setClauseLanguage(language)}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        clauseLanguage === language
                          ? "bg-card text-primary shadow-sm"
                          : "text-muted-foreground hover:text-primary"
                      }`}
                    >
                      {getLanguageLabel(language)}
                    </button>
                  ))}
                </div>
              )}
              {clauseTranslationPendingTargetLanguage ? (
                <div className="inline-flex items-center gap-2 text-sm text-muted">
                  <Loader2 size={14} className="animate-spin" />
                  {t("admin:standards.actions.translatingTo", {
                    language: getLanguageLabel(
                      clauseTranslationPendingTargetLanguage,
                    ),
                  })}
                </div>
              ) : clauseTranslateTargetLanguage ? (
                <Button
                  variant="secondary"
                  icon={RefreshCw}
                  onClick={handleRequestClauseTranslation}
                  loading={requestClauseTranslationMutation.isPending}
                >
                  {t("admin:standards.actions.translateTo", {
                    language: getLanguageLabel(clauseTranslateTargetLanguage),
                  })}
                </Button>
              ) : null}
            </div>
          )}
          <ClauseList
            clauses={standard.clauses.filter(
              (c) => (c.language ?? "en") === clauseLanguage,
            )}
          />
        </div>
      ) : null}

      {activeTab === "checklist" && showTabs && checklistData && (
        <div className="space-y-4">
          {(checklistAvailableLanguages.length > 1 ||
            checklistTranslateTargetLanguage ||
            checklistTranslationPendingTargetLanguage) && (
            <div className="flex flex-wrap items-center gap-3">
              {checklistAvailableLanguages.length > 1 && (
                <div className="flex gap-1 rounded-lg bg-muted/30 p-1 w-fit">
                  {checklistAvailableLanguages.map((language) => (
                    <button
                      key={language}
                      type="button"
                      onClick={() => setChecklistLanguage(language)}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        checklistLanguage === language
                          ? "bg-card text-primary shadow-sm"
                          : "text-muted-foreground hover:text-primary"
                      }`}
                    >
                      {getLanguageLabel(language)}
                    </button>
                  ))}
                </div>
              )}
              {checklistTranslationPendingTargetLanguage ? (
                <div className="inline-flex items-center gap-2 text-sm text-muted">
                  <Loader2 size={14} className="animate-spin" />
                  {t("admin:standards.actions.translatingTo", {
                    language: getLanguageLabel(
                      checklistTranslationPendingTargetLanguage,
                    ),
                  })}
                </div>
              ) : checklistTranslateTargetLanguage ? (
                <Button
                  variant="secondary"
                  icon={RefreshCw}
                  onClick={handleRequestChecklistTranslation}
                  loading={requestChecklistTranslationMutation.isPending}
                >
                  {t("admin:standards.actions.translateTo", {
                    language: getLanguageLabel(
                      checklistTranslateTargetLanguage,
                    ),
                  })}
                </Button>
              ) : null}
            </div>
          )}
          {(checklistReady || isPublished) && (
            <ValidationPanel
              standard={standard}
              checklistData={checklistData}
              checklistLanguage={checklistLanguage}
              onPublish={() => handleAction("publish")}
              isPublishPending={publishMutation.isPending}
            />
          )}
          <ChecklistAccordion data={checklistData} />
        </div>
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteMutation.isPending}
        title={t("admin:standards.deleteConfirm.title")}
        description={t("admin:standards.deleteConfirm.description")}
        confirmMatchText={standard.templateCode ?? standard.fileName}
        confirmLabel={t("admin:standards.deleteConfirm.confirmLabel")}
      />
    </div>
  );
}
